import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import ImageModal from '../shared/ImageModal';
import LazyImage from '../shared/LazyImage';
import Button from '../shared/Button';
import useCatalogData, { ProcessedImage } from '../../hooks/useCatalogData';

const FullGallery: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { images, loading: dataLoading } = useCatalogData(i18n.language);
  const [displayedImages, setDisplayedImages] = useState<ProcessedImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedImage, setSelectedImage] = useState<ProcessedImage | null>(null);
  
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: '100px',
  });

  const ITEMS_PER_PAGE = 6;

  // Load initial images
  useEffect(() => {
    if (images.length > 0) {
      const initial = images.slice(0, ITEMS_PER_PAGE);
      setDisplayedImages(initial);
      setHasMore(images.length > ITEMS_PER_PAGE);
    }
  }, [images]);

  // Load more images when user scrolls to bottom
  const loadMore = useCallback(async () => {
    if (loading || !hasMore || images.length === 0) return;

    setLoading(true);
    
    // Simulate API delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const currentLength = displayedImages.length;
    const nextImages = images.slice(
      currentLength, 
      currentLength + ITEMS_PER_PAGE
    );
    
    if (nextImages.length > 0) {
      setDisplayedImages(prev => [...prev, ...nextImages]);
      
      // Check if there are more images to load
      if (currentLength + nextImages.length >= images.length) {
        setHasMore(false);
      }
    } else {
      setHasMore(false);
    }
    
    setLoading(false);
  }, [loading, hasMore, displayedImages, images]);

  // Trigger load more when scrolling to bottom
  useEffect(() => {
    if (inView) {
      loadMore();
    }
  }, [inView, loadMore]);

  const handleImageClick = (image: ProcessedImage) => {
    setSelectedImage(image);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  if (dataLoading) {
    return (
      <section className="py-20 bg-dark-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="w-24 h-px bg-gold/30 mx-auto mb-8"></div>
              <div className="h-8 bg-dark-surface rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-dark-surface rounded w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="full-gallery" className="py-24 bg-dark-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Elegant separator */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '120px', opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/50 mx-auto mb-8"
          />

          <h2 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-light text-cream mb-6 tracking-wide">
            {t('fullGallery.title')}
          </h2>
          <p className="text-lg text-cream-dark max-w-3xl mx-auto font-light leading-relaxed">
            {t('fullGallery.subtitle')} ({images.length} {t('gallery.totalImages')})
          </p>

          {/* Bottom separator */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '80px', opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/30 mx-auto mt-8"
          />
        </motion.div>

        {/* Gallery grid with infinite scroll */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {displayedImages.map((image, index) => (
            <motion.div
              key={image.id}
              className="group cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: (index % ITEMS_PER_PAGE) * 0.1 }}
              onClick={() => handleImageClick(image)}
            >
              <div className="relative overflow-hidden bg-dark-surface border border-dark-border/20 hover:border-gold/30 transition-all duration-500 hover-lift">
                {/* Actual Image */}
                <div className="aspect-[4/5] relative">
                  <LazyImage
                    src={image.src}
                    alt={image.title}
                    className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                  />
                  
                  {/* Hover overlay with image info */}
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-primary/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="font-playfair text-sm text-cream mb-1 group-hover:text-gold/90 transition-colors duration-300 truncate">
                        {image.title}
                      </h3>
                      
                      <div className="w-8 h-px bg-gold/50 mb-2 group-hover:w-12 group-hover:bg-gold/70 transition-all duration-300"></div>
                      
                      <div className="flex items-center text-gold text-xs">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        <span className="font-light tracking-wider uppercase">
                          {t('gallery.viewDetails')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Load more indicator */}
        {hasMore && (
          <div ref={loadMoreRef} className="flex justify-center py-8">
            {loading ? (
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
                <span className="text-cream-dark font-light">{t('fullGallery.loadingMore')}</span>
              </div>
            ) : (
              <Button
                variant="secondary"
                onClick={loadMore}
                className="px-8 py-3 border border-gold/30 hover:border-gold hover:bg-gold/5 transition-all duration-300"
              >
                <span className="text-cream font-light tracking-wider uppercase text-sm">
                  {t('fullGallery.loadMore')}
                </span>
              </Button>
            )}
          </div>
        )}

        {/* No more items message */}
        {!hasMore && displayedImages.length > 0 && (
          <motion.div
            className="text-center py-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-16 h-px bg-gold/30 mx-auto mb-6"></div>
            <p className="text-cream-dark font-light mb-6">{t('fullGallery.noMore')}</p>
            <Button
              variant="primary"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="px-8 py-3 bg-gold/90 hover:bg-gold transition-all duration-300"
            >
              <span className="text-dark-primary font-medium tracking-wider uppercase text-sm">
                {t('fullGallery.backToTop')}
              </span>
            </Button>
          </motion.div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <ImageModal
          isOpen={!!selectedImage}
          onClose={handleCloseModal}
          src={selectedImage.src}
          alt={selectedImage.title}
          title={selectedImage.title}
          description={selectedImage.description}
          imageId={selectedImage.id}
        />
      )}
    </section>
  );
};

export default FullGallery;